import { title } from "process";

export enum Language {
    EN = "EN",
    SE = "SE",
}

export type Translations = typeof TRANSLATIONS_EN | typeof TRANSLATIONS_SE;

export const TRANSLATIONS_EN = {
    id: "ID",
    signIn: "Sign In",
    currentUser: "Current User",
    passwordForget: {
        subtitle: "Forgot your password? Click here.",
        resetPassword: "Reset Password",
        success: "Password reset successfully. Please log in with your new password.",
        afterSubmitInfo: "If the username exists, a reset link will be sent to the email associated with the account.",
        goBackToSignIn: "Go back to Sign In page",
    },
    signOut: "Sign Out",
    signedOut: "Signed Out",
    username: "Username",
    password: "Password",
    search: "Search",
    maxSearchHitsWarning: "Maximum search hits reached. Please refine your search to see other results.",
    switchLanguage: "Switch language",
    switchTheme: "Switch theme",
    common: {
        registerNew: "Register new",
        yes: "Yes",
        no: "No",
        ok: "OK",
        Cancel: "Cancel",
        All: "All",
        disabled: "Disabled",
        active: "Active",
        disable: "Disable",
        enable: "Enable",
        properties: {
            cardTitle: "Properties",
            cardDescription: "Custom properties",
            addProperty: "Add property",
            selectProperty: "Select property...",
            propertyKey: "Property",
            value: "Value",
            remove: "Remove",
        },
    },
    installation: {
        title: "Select installation to proceed",
        description: "Select the installation you want to proceed with, if you can't find the installation you are looking for, please contact your system administrator.",
        changeInstallation: "Change installation",
        currentInstallation: "Current installation",
    },
    save: {
        save: "Save",
        saved: {
            title: "Saved",
            description: "The changes have been saved",
        },
        errorsArePresent: {
            title: "Errors are present",
            description: "The changes cannot be saved because there are errors present. Please correct the errors and try again.",
            buttons: {
                cancel: "Cancel",
            },
        },
        warningsArePresent: {
            title: "There are warnings present",
            description: "Are you sure you want to save the changes?",
            buttons: {
                saveAnyway: "Save anyway",
                cancel: "Cancel",
            },
        },
    },
    sidebar: {
        administration: "Administration",
        configuration: "Configuration",
        importExport: "Import / Export",
        developer: "Developer",
        superAdmin: "Super Admin",
    },
    userManagement: {
        title: "User Management",
    },
    dashboard: {
        title: "Dashboard",
        totalWorksites: "Worksites",
        totalWorksitesSubtitle: "Total registered worksites",
        totalCustomers: "Customers",
        totalCustomersSubtitle: "Total registered customers",
        totalOrders: "Orders",
        totalOrdersSubtitle: "Total orders across all statuses",
        completedOrders: "Completed",
        completedOrdersSubtitle: "Total completed orders",
        createdOrders: "Created",
        createdOrdersSubtitle: "Orders awaiting processing",
        offeredOrders: "Offered",
        offeredOrdersSubtitle: "Quotes awaiting acceptance",
        invoicedOrders: "Invoiced",
        invoicedOrdersSubtitle: "Orders that have been invoiced",
        cancelledOrders: "Cancelled",
        cancelledOrdersSubtitle: "Orders that were cancelled",
        ordersByStatus: {
            title: "Orders by status",
            description: "Distribution of orders across all statuses",
        },
        completedJobsPerAgent: {
            title: "Completed per resource",
            description: "Completed orders by the resource that did them",
        },
        greeting: (part: "morning" | "day" | "evening", name: string) =>
            `${part === "morning" ? "Good morning" : part === "day" ? "Good afternoon" : "Good evening"}${name ? `, ${name}` : ""}`,
        todayTasks: "Tasks today",
        todayTasksSubtitle: "Open tasks scheduled for today",
        openTasks: "Waiting to be done",
        ofAllOrders: (share: string) => `${share} of all orders`,
        total: "Total",
        orders: (n: number) => `${n} ${n === 1 ? "order" : "orders"}`,
        noData: "Nothing here yet.",
        noResourceData: "No completed orders with a resource yet.",
        view: "View",
    },
    commandPalette: {
        placeholder: "Search pages…",
        search: "Search",
        pages: "Pages",
        create: "Create",
        newOrder: "New order",
        newCustomer: "New customer",
        newWorksite: "New worksite",
        noResults: "No pages match the search.",
        move: "move",
        open: "open",
        toggle: "open / close",
    },
    header: {
        product: "Work order management",
        account: "Account",
        switchInstallation: "Switch company",
    },
    graph: {
        title: "Overview",
    },
    worksites: {
        title: "Worksites",
    },
    customers: {
        title: "Customers",
    },
    orders: {
        title: "Orders",
    },
    tasks: {
        title: "Tasks",
        description: "Tasks overview for the selected resource and date range.",
        noTasksFound: "No tasks found for the selected resource(s) and date.",
        worksitePhone: "Worksite phone",
        customerPhone: "Customer phone",
        nextRoute: (n: number) => `Next route (${n} ${n === 1 ? "address" : "addresses"})`,
        noRoute: "No open tasks to route",
        onHold: "On hold",
        holdReason: "Reason",
        dispatcherNotes: "Office note",
        recurring: "Recurring",
        comment: "Comment",
        commentPlaceholder: "Note for the office (required to pause)",
        navigate: "Navigate",
        pause: "Pause",
        resume: "Resume",
        complete: "Complete",
        openOrder: "Open order",
        confirmTitle: "Complete task?",
        confirmButton: "Yes, complete",
        missing: "missing",
        extra: "extra",
        address: "Address",
        pauseTooShort: (n: number) => `Write a comment of at least ${n} characters to pause the task`,
        completed: "Task completed",
        paused: "Task paused",
        resumed: "Task resumed",
        negative: "Counts cannot be negative",
    },
    products: {
        title: "Products",
    },
    resources: {
        title: "Resources",
    },
    properties: {
        title: "Properties",
    },
    systemSettings: {
        title: "System settings",
        description: "System settings that affect the entire system which can be used to configure the system to your needs",
        product: {
            title: "Products",
            description: "The products that can be used in an order",
            create: "Create new product",
        },
        resource: {
            title: "Resources",
            description: "The resources that can be utilized by an employee",
            create: "Create new resource",
        },
        companySettings: {
            title: "Company settings",
            description: "Company settings that affect the entire system",
            save: "Save company settings",
        },
        propertyKey: {
            title: "Custom Properties",
            description: "Define custom properties for worksites, orders and customers",
            create: "Create new property",
        },
    },
    overview: {
        title: "Overview",
        description: "Overview of allocated resources and planned orders. Note that only orders that have the status 'Created' are shown here.",
        loading: "Loading overview data...",
        performingAction: "Performing action...",
        movedSuccessfully: {
            title: "Rescheduled",
            description: "The order has been rescheduled successfully",
        },
        priorityUpdatedSuccessfully: {
            title: "Priority Updated",
            description: "The priority of the order has been updated successfully",
        },
        week: "Week",
        weeks: "Weeks",
        refetching: {
            seconds: "Updating in {seconds} seconds...",
            minutes: "Updating in {minutes} minutes...",
        }
    },
    attachedFiles: {
        title: "Attachment(s)",
        description: "Files that are attached to this object",
        remove: "Remove attachment",
        attach: "Attach file",
        cannotPreview: "Cannot preview this file type.",
        downloadFile: "Download file",
        filePreview: "File Preview",
        newPreviewDisabled: {
            title: "Preview disabled for unsaved attachments",
            description: "Preview is not available for unsaved attachments. Please save the object first.",
        },
    },
    model: {
        propertyKey: {
            id: "ID",
            objectType: "Object type",
            key: "Key",
            propertyType: "Type",
            delete: "Delete",
            search: {
                title: "Custom Properties",
                search: "Search",
            },
        },
        product: {
            id: "ID",
            code: "Code",
            disable: "Disable product",
            enable: "Enable product",
            modify: "Modify product",
            delete: "Delete product",
            search: {
                title: "Products",
                search: "Search",
            },
            tasksCard: {
                title: "Tasks page",
                description: "Products switched on here show their count on every task card, and crew can change the count when they complete a task. If no product is switched on, task cards show every product on the order.",
            },
            showOnTasks: "Show on Tasks",
            taskIcon: "Icon",
            taskSort: "Order (lowest first)",
            iconPicker: {
                title: "Choose icon",
                change: "Change icon",
                remove: "Remove icon",
                none: "None",
                search: "Search, e.g. bin, bag, broom",
                all: "All",
                noResults: "No icons match the search.",
                categories: {
                    waste: "Waste & recycling",
                    cleaning: "Cleaning & water",
                    buildings: "Buildings & rooms",
                    access: "Access",
                    vehicles: "Vehicles & transport",
                    tools: "Tools & service",
                    outdoor: "Outdoor",
                    items: "Items & packaging",
                    people: "People & symbols",
                },
            },
        },
        resource: {
            id: "ID",
            code: "Code",
            disable: "Disable resource",
            enable: "Enable resource",
            modify: "Modify resource",
            delete: "Delete resource",
            search: {
                title: "Resources",
                search: "Search",
            },
        },
        companySettings: {
            id: "Company ID",
            name: "Company name",
            mapboxToken: "Mapbox API token",
            geocodingToken: "Geocoding API token",
            geoCodingTokenMissing: {
                title: "Geocoding API token missing",
                description: "Add the geocoding API token to the company settings to use this feature.",
            },
            mapboxHint: "Public token (starts with pk.). It is sent to every user's browser to draw maps, so limit it to this site's address in your Mapbox account.",
            secretsTitle: "API keys",
            secretsDescription: "Kept on the server and never shown in the browser. You can replace or remove a key, but not read it back.",
            secretSet: (hint: string | null) => `Set${hint ? ` (${hint})` : ""}`,
            secretNotSet: "Not set",
            secretPlaceholder: "Paste a new key",
            secretSave: "Save key",
            secretRemove: "Remove key",
            secretSaved: "Key saved",
            secretRemoved: "Key removed",
        },
        customer: {
            customer: "Customer",
            cards: {
                baseInfo: {
                    title: "Base information",
                    description: "Basic information about the customer",
                },
                identificationInfo: {
                    title: "Identification information",
                    description: "Identification information for the customer",
                },
                contactInfo: {
                    title: "Contact information",
                    description: "Contact information for the customer",
                },
                addressInfo: {
                    title: "Address information",
                    description: "Address information for the customer",
                },
                notes: {
                    title: "Notes",
                    description: "Additional notes about the customer",
                },
                worksites: {
                    title: "Worksites",
                    description: "Worksites associated with the customer",
                },
            },
            search: {
                title: "Customers",
                description: "Search for a customer by name, external ID or internal ID",
                search: "Search",
            },
            id: "ID",
            externalId: "External ID",
            isCompany: "Company",
            name: "Name",
            companyName: "Company name",
            firstName: "First name",
            lastName: "Last name",
            fullName: "Full name",
            identificationNumber: {
                id: "Identification number",
                organizationNumber: "Organization number",
                ssn: "SSN",
            },
            vatNumber: "VAT number",
            primaryContact: "Primary contact",
            email: "Email",
            phone: "Phone",
            address: "Address",
            postalCode: "Postal code",
            city: "City",
            notes: "Notes",
            validationRules: {
                companyCannotHaveLastName: "A company should not have a last name",
                firstNameIsRequired: "First name is required for an individual",
                lastNameIsRequired: "Last name is required for an individual",
                primaryContactShouldBeSetForCompany: "Primary contact should be set for a company",
                emailIsSuggested: "An email should be provided",
                phoneIsSuggested: "A phone number should be provided",
                companyMustHaveIdentificationNumber: "A company must have an organization number",
            },
        },
        worksite: {
            id: "ID",
            createOrder: "Create order",
            worksite: "Worksite",
            externalId: "External ID",
            identifier: "Identifier",
            name: "Name",
            address: "Address",
            postalCode: "Postal code",
            city: "City",
            notes: "Notes",
            contactPerson: "Extra contact person",
            contactEmail: "Extra contact email",
            contactPhone: "Extra contact phone",
            latitude: "Latitude",
            longitude: "Longitude",
            customer: "Customer",
            noCustomerConnected: "No customer connected",
            noWorksiteSoNoCustomer: "No worksite connected, please connect a worksite to see the customer",
            validationRules: {
                externalIdIsRequired: "External ID is required",
                customerIsRequired: "Customer is required",
                addressIsRequired: "Address is required",
                cityIsRequired: "City is required",
                postalCodeIsRequired: "Postal code is required",
            },
            search: {
                title: "Worksites",
                description: "Search for a worksite by name, external ID or internal ID",
                search: "Search",
            },
            cards: {
                baseInfo: {
                    title: 'Base Information',
                    description: 'Basic information about the worksite',
                },
                identificationInfo: {
                    title: 'Identification Information',
                    description: 'Identification information for the worksite',
                },
                contactInfo: {
                    title: 'Contact Information',
                    description: 'Contact information for the worksite',
                },
                map: {
                    title: 'Map',
                    description: 'Map of the worksite',
                    loadMap: "Load map",
                    findWorksite: "Find worksite",
                    totalMarkers: "Total markers",
                    customMarkers: {
                        title: "Custom markers",
                        description: "Custom markers defined by the user",
                    },
                    coordinatesAlreadyExist: {
                        title: "Coordinates already exist",
                        description: "Coordinates already exist for this worksite, recalculating the coordinates will overwrite the existing coordinates. Do you want to continue?",
                    },
                    coordinatesNotFound: {
                        title: "Coordinates not found",
                        description: "Coordinates not found for this worksite, please check the address and try again or manually enter the coordinates",
                    },
                    setMarkerToCenter: "Set marker to center",
                },
                notes: {
                    title: 'Notes',
                    description: 'Additional notes about the worksite',
                },
            },

        },
        order: {
            id: "ID",
            copyOrder: "Copy order",
            externalId: "External ID",
            scheduledDate: "Scheduled date",
            scheduledDateFrom: "Scheduled date from",
            scheduledDateTo: "Scheduled date to",
            completionDate: "Completion date",
            noWorksiteConnected: "No worksite connected",
            resource: "Resource",
            allResources: "All resources",
            filterDate: "Date",
            status: "Status",
            recurringInterval: "Interval (Months) - 0 for single order",
            contractEndDate: "Contract end date",
            futureOrders: "Future orders",
            thisOrder: "This order",
            manualDate: "Manual date, click to remove override",
            express: {
                title: "Express",
                markExpress: "Mark as express",
                markNormal: "Mark as normal",
            },
            statusOptions: {
                CREATED: "Created",
                CANCELLED: "Cancelled",
                COMPLETED: "Completed",
                INVOICED: "Invoiced",
                OFFERED: "Offer made",
            },
            sortOrder: "Sort",
            sortOrderOptions: {
                SCHEDULED_DESC: "Scheduled date (latest first)",
                SCHEDULED_ASC: "Scheduled date (oldest first)",
                COMPLETION_DESC: "Completion date (latest first)",
                COMPLETION_ASC: "Completion date (oldest first)",
                CREATED_DESC: "Latest created",
            },
            search: {
                title: "Orders",
                description: "Search for an order by external ID, Scheduled date or Completion date",
                search: "Search",
            },
            validationRules: {
                // shouldHaveExternalId: "An order should have an external ID",
                worksiteIsRequired: "An order must be associated with a worksite",
                scheduledDateIsRequired: "An order must be scheduled for a date",
                fromTasks: {
                    reminderComplete: "If the order is fully complete, remember to set the status to 'Completed'",
                    rowsShouldMatch: "The actual quantities in the order rows should match the ordered quantities unless there is a specific reason",
                    reminderCompletionDateShouldBeToday: "If the order is being completed today, remember to set the completion date to today",
                },
                completionDateShouldOnlyBeSetWhenCompleted: "Completion date should only be set when after the order status is 'Completed'",
                completionDateMustBeSetWhenCompleted: "Completion date must be set when the order status is 'Completed'",
                selectedWorksiteIsInactive: "The selected worksite is inactive. Cannot create an order at an inactive worksite manually.",
            },
            warnings: {
                productMustBeUnique: {
                    title: "Product must be unique",
                    description: "A product can only be added once to an order row. Please modify the quantity of the existing row instead.",
                },
            },
            products: "Products",
            orderedProducts: "Ordered {count} products",
            receivedProducts: "Received {count} products",
            noProducts: "No products in order",
            productSummary: {
                title: "Order Summary",
                product: "Product",
                orderedQuantity: "Ordered",
                actualQuantity: "Received",
            },
            cards: {
                customer: {
                    title: "Customer",
                    description: "Customer associated with the order",
                },
                worksite: {
                    title: "Worksite",
                    description: "Worksite associated with the order",
                },
                baseInfo: {
                    title: 'Base Information',
                    description: 'Basic information about the order',
                },
                recurringDetails: {
                    title: 'Recurring details',
                    description: 'Details about the recurring information of the order',
                },
                notes: {
                    title: 'Notes',
                    description: 'Additional notes about the order',
                    dispatcherNotes: "Dispatcher notes",
                    employeeNotes: "Employee notes",
                },
                holdInfo: {
                    title: 'Hold information',
                    description: 'Information about held orders that are considered paused',
                    hold: "Hold",
                    reason: "Reason for hold",
                },
                orderRows: {
                    title: "Order rows",
                    description: "Order rows associated with the order",
                    product: "Product",
                    orderedQuantity: "Ordered quantity",
                    actualQuantity: "Actual quantity",
                    removeRow: "Remove row",
                    editRow: "Edit row",
                    addRow: "Add row",
                },
            }
        },
        user: {
            id: "ID",
            username: "Username",
            firstName: "First name",
            lastName: "Last name",
            name: "Name",
            email: "Email",
            phone: "Phone",
            enabled: "Enabled",
            role: "Role",
            addInstance: "Add instance",
            removeInstance: "Remove instance",
            cards: {
                userInfo: {
                    title: "User information",
                    description: "Basic information about the user",
                },
                contactInfo: {
                    title: "Contact information",
                    description: "Contact information for the user",
                },
                accountInfo: {
                    title: "Account",
                    description: "Account settings and role",
                },
                password: {
                    title: "Password",
                    description: "Email the user a link to choose a new password. Their current password keeps working until they do.",
                    send: "Send password reset email",
                    confirmTitle: "Send password reset?",
                    confirmBody: (who: string) => `${who} gets an email with a link to choose a new password. The link is valid for 24 hours.`,
                    confirm: "Send email",
                    sent: "Password reset email sent",
                },
                instances: {
                    title: "Instances",
                    description: "Instances the user has access to",
                },
            },
            search: {
                title: "Users",
                description: "Search for a user by username or name",
                search: "Search",
            },
            validationRules: {
                usernameIsRequired: "Username is required",
            },
        },
    },
    importExport: {
        customerImport: "Customer import",
        worksiteImport: "Worksite import",
        status: "Status",
        import: "Import",
        importAll: "Import all rows",
        addRows: "Add 10 more rows",
        copyTemplate: "Copy template",
        excelImport: "Drop Excel/CSV file here or click to upload",
        notYetImported: "Not yet imported",
        imported: "Imported",
        failed: "Failed",
        customerId: "Customer ID",
        importing: "Importing...",
        templateCopied: "Column names copied to clipboard",
        rowsAdded: "rows added from file",
        noDataInFile: "No data found in file",
        createFromClipboard: "Create from clipboard",
    },
    templateEditor: {
        title: "Template Editor",
        description: "Edit and preview email templates",
        selectTemplate: "Template",
        selectTemplatePlaceholder: "Select a template...",
        subject: "Subject",
        content: "Content",
        preview: "Preview",
        placeholders: "Available Placeholders",
        placeholdersHint: "Edit the sample values to preview different data. Clear a value to see how {{#section}} rows hide.",
        placeholdersIgnored: "Previewing with a real order — the sample values below are ignored.",
        previewOrder: "Preview with order (optional)",
        previewOrderPlaceholder: "Use sample values…",
        placeholder: "Placeholder",
        exampleValue: "Example Value",
    },
};

export const TRANSLATIONS_SE: typeof TRANSLATIONS_EN = {
    id: "ID",
    signIn: "Logga in",
    passwordForget: {
        subtitle: "Glömt ditt lösenord? Klicka här.",
        resetPassword: "Återställ lösenord",
        success: "Lösenordet har återställts. Logga in med ditt nya lösenord.",
        afterSubmitInfo: "Om användarnamnet finns, kommer en återställningslänk att skickas till den e-postadress som är kopplad till kontot.",
        goBackToSignIn: "Gå tillbaka till inloggningssidan",
    },
    currentUser: "Inloggad användare",
    signOut: "Logga ut",
    signedOut: "Loggades ut",
    username: "Användarnamn",
    password: "Lösenord",
    search: "Sök",
    maxSearchHitsWarning: "Maximalt antal sökträffar uppnått. Vänligen förfina din sökning för att se andra resultat.",
    switchLanguage: "Byt språk",
    switchTheme: "Byt tema",
    common: {
        registerNew: "Registrera ny",
        yes: "Ja",
        no: "Nej",
        ok: "OK",
        Cancel: "Avbryt",
        All: "Alla",
        disabled: "Inaktiverad",
        active: "Aktiv",
        disable: "Inaktivera",
        enable: "Aktivera",
        properties: {
            cardTitle: "Egenskaper",
            cardDescription: "Anpassade egenskaper",
            addProperty: "Lägg till egenskap",
            selectProperty: "Välj egenskap...",
            propertyKey: "Egenskap",
            value: "Värde",
            remove: "Ta bort",
        },
    },
    installation: {
        title: "Välj installation för att fortsätta",
        description: "Välj den installation du vill fortsätta med, om du inte hittar den installation du letar efter, vänligen kontakta din systemadministratör.",
        changeInstallation: "Byt installation",
        currentInstallation: "Nuvarande installation",
    },
    save: {
        save: "Spara",
        saved: {
            title: "Sparat",
            description: "Ändringarna har sparats",
        },
        errorsArePresent: {
            title: "Fel finns",
            description: "Ändringarna kan inte sparas eftersom det finns fel. Korrigera felen och försök igen.",
            buttons: {
                cancel: "Avbryt",
            },
        },
        warningsArePresent: {
            title: "Det finns varningar",
            description: "Är du säker på att du vill spara ändringarna?",
            buttons: {
                saveAnyway: "Spara ändå",
                cancel: "Avbryt",
            },
        },
    },
    sidebar: {
        administration: "Administration",
        configuration: "Konfiguration",
        importExport: "Import / Export",
        developer: "Utvecklare",
        superAdmin: "Superadmin",
    },
    userManagement: {
        title: "Användarhantering",
    },
    dashboard: {
        title: "Instrumentpanel",
        totalWorksites: "Arbetsplatser",
        totalWorksitesSubtitle: "Totalt registrerade arbetsplatser",
        totalCustomers: "Kunder",
        totalCustomersSubtitle: "Totalt registrerade kunder",
        totalOrders: "Ordrar",
        totalOrdersSubtitle: "Totalt antal ordrar i alla statusar",
        completedOrders: "Slutförda",
        completedOrdersSubtitle: "Totalt antal slutförda ordrar",
        createdOrders: "Skapade",
        createdOrdersSubtitle: "Ordrar som väntar på behandling",
        offeredOrders: "Offererade",
        offeredOrdersSubtitle: "Offerter som väntar på svar",
        invoicedOrders: "Fakturerade",
        invoicedOrdersSubtitle: "Ordrar som har fakturerats",
        cancelledOrders: "Avbrutna",
        cancelledOrdersSubtitle: "Ordrar som har avbrutits",
        ordersByStatus: {
            title: "Ordrar per status",
            description: "Fördelning av ordrar över alla statusar",
        },
        completedJobsPerAgent: {
            title: "Slutförda per resurs",
            description: "Slutförda order per resurs som utförde dem",
        },
        greeting: (part: "morning" | "day" | "evening", name: string) =>
            `${part === "morning" ? "God morgon" : part === "day" ? "God eftermiddag" : "God kväll"}${name ? `, ${name}` : ""}`,
        todayTasks: "Uppgifter idag",
        todayTasksSubtitle: "Öppna uppgifter planerade idag",
        openTasks: "Väntar på utförande",
        ofAllOrders: (share: string) => `${share} av alla order`,
        total: "Totalt",
        orders: (n: number) => `${n} order`,
        noData: "Inget här än.",
        noResourceData: "Inga slutförda order med resurs än.",
        view: "Visa",
    },
    commandPalette: {
        placeholder: "Sök sidor…",
        search: "Sök",
        pages: "Sidor",
        create: "Skapa",
        newOrder: "Ny order",
        newCustomer: "Ny kund",
        newWorksite: "Ny arbetsplats",
        noResults: "Inga sidor matchar sökningen.",
        move: "flytta",
        open: "öppna",
        toggle: "öppna / stäng",
    },
    header: {
        product: "Ordersystem",
        account: "Konto",
        switchInstallation: "Byt företag",
    },
    graph: {
        title: "Översikt",
    },
    worksites: {
        title: "Arbetsplatser",
    },
    customers: {
        title: "Kunder",
    },
    orders: {
        title: "Ordrar",
    },
    tasks: {
        title: "Uppgifter",
        description: "Uppgiftsöversikt för den valda resursen och datumintervallet.",
        noTasksFound: "Inga uppgifter hittades för den valda resursen och datumet.",
        worksitePhone: "Arbetsplatsens telefon",
        customerPhone: "Kundens telefon",
        nextRoute: (n: number) => `Nästa rutt (${n} ${n === 1 ? "adress" : "adresser"})`,
        noRoute: "Inga öppna uppgifter att planera rutt för",
        onHold: "Pausad",
        holdReason: "Orsak",
        dispatcherNotes: "Notering från kontoret",
        recurring: "Återkommande",
        comment: "Kommentar",
        commentPlaceholder: "Notering till kontoret (krävs för att pausa)",
        navigate: "Navigera",
        pause: "Pausa",
        resume: "Återuppta",
        complete: "Slutför",
        openOrder: "Öppna order",
        confirmTitle: "Slutföra uppgiften?",
        confirmButton: "Ja, slutför",
        missing: "saknas",
        extra: "extra",
        address: "Adress",
        pauseTooShort: (n: number) => `Skriv en kommentar på minst ${n} tecken för att pausa uppgiften`,
        completed: "Uppgiften är slutförd",
        paused: "Uppgiften är pausad",
        resumed: "Uppgiften är återupptagen",
        negative: "Antal kan inte vara negativa",
    },
    products: {
        title: "Produkter",
    },
    resources: {
        title: "Resurser",
    },
    properties: {
        title: "Egenskaper",
    },
    systemSettings: {
        title: "Systeminställningar",
        description: "Systeminställningar som påverkar hela systemet och som kan användas för att konfigurera systemet efter dina behov",
        product: {
            title: "produkt",
            description: "De produkter som kan användas i en order",
            create: "Skapa ny produkt",
        },
        resource: {
            title: "Resurser",
            description: "De resurser som kan användas av en anställd",
            create: "Skapa ny resurs",
        },
        companySettings: {
            title: "Företagsinställningar",
            description: "Företagsinställningar som påverkar hela systemet",
            save: "Spara företagsinställningar",
        },
        propertyKey: {
            title: "Anpassade egenskaper",
            description: "Definiera anpassade egenskaper för arbetsplatser, ordrar och kunder",
            create: "Skapa ny egenskap",
        },
    },
    overview: {
        title: "Översikt",
        description: "Översikt över allokerade resurser och planerade jobb. Observera att endast ordrar som har statusen 'Skapad' visas här.",
        loading: "Laddar översiktsdata...",
        performingAction: "Utför åtgärd...",
        movedSuccessfully: {
            title: "Omplanerad",
            description: "Omplaneringen lyckades",
        },
        priorityUpdatedSuccessfully: {
            title: "Prioritet uppdaterad",
            description: "Prioriteten för ordern har uppdaterats",
        },
        week: "Vecka",
        weeks: "Veckor",
        refetching: {
            seconds: "Uppdaterar om {seconds} sekunder...",
            minutes: "Uppdaterar om {minutes} minuter...",
        }
    },
    attachedFiles: {
        title: "Bilagor",
        description: "Filer som är bifogade till detta objekt",
        remove: "Ta bort bilaga",
        attach: "Bifoga fil",
        cannotPreview: "Kan inte förhandsgranska den här filtypen.",
        downloadFile: "Ladda ner fil",
        filePreview: "Filförhandsvisning",
        newPreviewDisabled: {
            title: "Förhandsgranskning inaktiverad för osparade bilagor",
            description: "Förhandsgranskning är inte tillgänglig för osparade bifogade filer. Spara objektet först.",
        }
    },
    model: {
        propertyKey: {
            id: "ID",
            objectType: "Objekttyp",
            key: "Nyckel",
            propertyType: "Typ",
            delete: "Ta bort",
            search: {
                title: "Anpassade egenskaper",
                search: "Sök",
            },
        },
        product: {
            id: "ID",
            code: "Kod",
            disable: "avaktivera produkt",
            enable: "Aktivera produkt",
            modify: "Modifiera produkt",
            delete: "Radera produkt",
            search: {
                title: "Produkter",
                search: "Sök",
            },
            tasksCard: {
                title: "Uppgiftssidan",
                description: "Produkter som slås på här visar sitt antal på varje uppgiftskort, och personalen kan ändra antalet när de slutför en uppgift. Om ingen produkt är påslagen visar korten alla produkter på ordern.",
            },
            showOnTasks: "Visa på Uppgifter",
            taskIcon: "Ikon",
            taskSort: "Ordning (lägst först)",
            iconPicker: {
                title: "Välj ikon",
                change: "Byt ikon",
                remove: "Ta bort ikon",
                none: "Ingen",
                search: "Sök, t.ex. kärl, påse, kvast",
                all: "Alla",
                noResults: "Inga ikoner matchar sökningen.",
                categories: {
                    waste: "Avfall & återvinning",
                    cleaning: "Rengöring & vatten",
                    buildings: "Byggnader & rum",
                    access: "Tillträde",
                    vehicles: "Fordon & transport",
                    tools: "Verktyg & service",
                    outdoor: "Utomhus",
                    items: "Artiklar & förpackning",
                    people: "Personer & symboler",
                },
            },
        },
        resource: {
            id: "ID",
            code: "Kod",
            disable: "avaktivera resurs",
            enable: "Aktivera resurs",
            modify: "Modifiera resurs",
            delete: "Radera resurs",
            search: {
                title: "Resurser",
                search: "Sök",
            },
        },
        companySettings: {
            id: "ID",
            name: "Företagsnamn",
            mapboxToken: "Mapbox API token",
            geocodingToken: "Geokod API token",
            geoCodingTokenMissing: {
                title: "Geokod API-token saknas",
                description: "Lägg till geokod API-token i företagsinställningarna för att använda den här funktionen.",
            },
            mapboxHint: "Publik token (börjar med pk.). Den skickas till alla användares webbläsare för att rita kartor, så begränsa den till den här webbplatsens adress i ditt Mapbox-konto.",
            secretsTitle: "API-nycklar",
            secretsDescription: "Sparas på servern och visas aldrig i webbläsaren. Du kan byta eller ta bort en nyckel, men inte läsa den igen.",
            secretSet: (hint: string | null) => `Angiven${hint ? ` (${hint})` : ""}`,
            secretNotSet: "Inte angiven",
            secretPlaceholder: "Klistra in en ny nyckel",
            secretSave: "Spara nyckel",
            secretRemove: "Ta bort nyckel",
            secretSaved: "Nyckeln är sparad",
            secretRemoved: "Nyckeln är borttagen",
        },
        customer: {
            customer: "Kund",
            cards: {
                baseInfo: {
                    title: "Grundinformation",
                    description: "Grundläggande information om kunden",
                },
                identificationInfo: {
                    title: "Identiferingsinformation",
                    description: "Identiferingsinformation för kunden",
                },
                contactInfo: {
                    title: "Kontaktinformation",
                    description: "Kontaktinformation för kunden",
                },
                addressInfo: {
                    title: "Adressinformation",
                    description: "Adressinformation för kunden",
                },
                notes: {
                    title: "Anteckningar",
                    description: "Ytterligare anteckningar om kunden",
                },
                worksites: {
                    title: "Arbetsplatser",
                    description: "Arbetsplatser associerade med kunden",
                },
            },
            search: {
                title: "Kunder",
                description: "Sök efter en kund efter namn, extern ID eller internt ID",
                search: "Sök",
            },
            id: "ID",
            externalId: "Extern ID",
            isCompany: "Företag",
            name: "Namn",
            companyName: "Företagsnamn",
            firstName: "Förnamn",
            lastName: "Efternamn",
            fullName: "Fullständigt namn",
            identificationNumber: {
                id: "Identifikationsnummer",
                organizationNumber: "Organisationsnummer",
                ssn: "Personnummer",
            },
            vatNumber: "Momsnummer",
            primaryContact: "Primär kontakt",
            email: "E-post",
            phone: "Telefon",
            address: "Adress",
            postalCode: "Postnummer",
            city: "Stad",
            notes: "Anteckningar",
            validationRules: {
                companyCannotHaveLastName: "Ett företag ska inte ha ett efternamn",
                firstNameIsRequired: "Förnamn krävs för en privatperson",
                lastNameIsRequired: "Efternamn krävs för en privatperson",
                primaryContactShouldBeSetForCompany: "Primär kontakt ska anges för ett företag",
                emailIsSuggested: "E-postadress bör anges",
                phoneIsSuggested: "Telefonnummer bör anges",
                companyMustHaveIdentificationNumber: "Ett företag måste ha ett organisationsnummer",
            },
        },
        worksite: {
            id: "ID",
            createOrder: "Skapa order",
            externalId: "Extern ID",
            identifier: "Märkning",
            worksite: "Arbetsplats",
            name: "Namn",
            address: "Adress",
            postalCode: "Postnummer",
            city: "Stad",
            notes: "Anteckningar",
            contactPerson: "Kontaktperson",
            contactEmail: "Kontakt e-post",
            contactPhone: "Kontakt telefon",
            latitude: "Latitud",
            longitude: "Longitud",
            customer: "Kund",
            noCustomerConnected: "Ingen kund kopplad",
            noWorksiteSoNoCustomer: "Ingen arbetsplats kopplad, vänligen koppla en arbetsplats för att se kunden",
            validationRules: {
                externalIdIsRequired: "Extern ID krävs",
                customerIsRequired: "Kund krävs",
                addressIsRequired: "Adress krävs",
                cityIsRequired: "Stad krävs",
                postalCodeIsRequired: "Postnummer krävs",
            },
            search: {
                title: "Arbetsplatser",
                description: "Sök efter en arbetsplats efter namn, externt ID eller internt ID",
                search: "Sök",
            },
            cards: {
                baseInfo: {
                    title: 'Grundinformation',
                    description: 'Grundläggande information om arbetsplatsen',
                },
                identificationInfo: {
                    title: 'Identifikationsinformation',
                    description: 'Identifikationsinformation för arbetsplatsen',
                },
                contactInfo: {
                    title: 'Kontaktinformation',
                    description: 'Kontaktinformation för arbetsplatsen',
                },
                map: {
                    title: 'Karta',
                    description: 'Karta över arbetsplatsen',
                    loadMap: "Ladda karta",
                    findWorksite: "Hitta arbetsplats",
                    totalMarkers: "Totalt antal markörer",
                    customMarkers: {
                        title: "Anpassade markörer",
                        description: "Anpassade markörer definierade av användaren",
                    },
                    coordinatesAlreadyExist: {
                        title: "Koordinater finns redan",
                        description: "Koordinater finns redan för denna arbetsplats, att beräkna om koordinaterna kommer att skriva över de befintliga koordinaterna. Vill du fortsätta?",
                    },
                    coordinatesNotFound: {
                        title: "Koordinater hittades inte",
                        description: "Koordinater hittades inte för denna arbetsplats, kontrollera adressen och försök igen eller ange koordinaterna manuellt",
                    },
                    setMarkerToCenter: "Ställ markören till mitten",
                },
                notes: {
                    title: 'Anteckningar',
                    description: 'Ytterligare anteckningar om arbetsplatsen',
                },
            },
        },
        order: {
            id: "ID",
            copyOrder: "Kopiera order",
            externalId: "Extern ID",
            scheduledDate: "Planerat datum",
            scheduledDateFrom: "Planerat datum från",
            scheduledDateTo: "Planerat datum till",
            completionDate: "Slutförandedatum",
            resource: "Resurs",
            allResources: "Alla resurser",
            filterDate: "Datum",
            status: "Status",
            recurringInterval: "Intervall (Månader) - 0 för enkel order",
            contractEndDate: "Avtals slutdatum",
            futureOrders: "Framtida order",
            thisOrder: "Nuvarande order",
            manualDate: "Manuellt datum, klicka för att ta bort överskrivning",
            noWorksiteConnected: "Ingen arbetsplats kopplad",
            express: {
                title: "Express",
                markExpress: "Markera som express",
                markNormal: "Markera som normal",
            },
            statusOptions: {
                CREATED: "Skapad",
                CANCELLED: "Avbruten",
                COMPLETED: "Avslutad",
                INVOICED: "Fakturerad",
                OFFERED: "Offert skapad",
            },
            sortOrder: "Sortering",
            sortOrderOptions: {
                SCHEDULED_DESC: "Planerat datum (senaste först)",
                SCHEDULED_ASC: "Planerat datum (äldsta först)",
                COMPLETION_DESC: "Slutförandedatum (senaste först)",
                COMPLETION_ASC: "Slutförandedatum (äldsta först)",
                CREATED_DESC: "Senast skapade",
            },
            search: {
                title: "Ordrar",
                description: "Sök efter en order efter externt ID eller internt ID",
                search: "Sök",
            },
            validationRules: {
                // shouldHaveExternalId: "En order bör ha ett externt ID",
                worksiteIsRequired: "En order måste vara associerad med en arbetsplats",
                scheduledDateIsRequired: "En order måste vara schemalagd för ett datum",
                fromTasks: {
                    reminderComplete: "Om ordern är helt slutförd, kom ihåg att ställa in statusen till 'Avslutad'",
                    rowsShouldMatch: "De faktiska antalen i orderraderna bör matcha de beställda antalen om det inte finns en specifik anledning",
                    reminderCompletionDateShouldBeToday: "Om ordern slutförs idag, kom ihåg att ställa in slutförandedatumet till idag",
                },
                completionDateShouldOnlyBeSetWhenCompleted: "Slutförandedatum bör endast anges efter orderstatus är 'Avslutad'",
                completionDateMustBeSetWhenCompleted: "Slutförandedatum måste anges när orderstatus är 'Avslutad'",
                selectedWorksiteIsInactive: "Den valda arbetsplatsen är inaktiv. Kan inte skapa en order på en inaktiv arbetsplats manuellt.",
            },
            warnings: {
                productMustBeUnique: {
                    title: "Produkten måste vara unik",
                    description: "En produkt kan endast läggas till en gång i en orderrad. Vänligen ändra kvantiteten för den befintliga raden istället.",
                },
            },
            products: "Produkter",
            orderedProducts: "Beställt {count} produkter",
            receivedProducts: "Mottaget {count} produkter",
            noProducts: "Inga produkter i ordern",
            productSummary: {
                title: "Ordersammanfattning",
                product: "Produkt",
                orderedQuantity: "Beställt",
                actualQuantity: "Mottaget",
            },
            cards: {
                customer: {
                    title: "Kund",
                    description: "Kund associerad med ordern",
                },
                worksite: {
                    title: "Arbetsplats",
                    description: "Arbetsplats associerad med ordern",
                },
                baseInfo: {
                    title: 'Grundinformation',
                    description: 'Grundläggande information om ordern',
                },
                recurringDetails: {
                    title: 'Detaljer för återkommande',
                    description: 'Detaljer om den återkommande information av ordern',
                },
                notes: {
                    title: 'Anteckningar',
                    description: 'Ytterligare anteckningar om ordern',
                    dispatcherNotes: "Dispatcheranteckningar",
                    employeeNotes: "Anställdanteckningar",
                },
                holdInfo: {
                    title: 'Blockeringsinformation',
                    description: 'Information om order som är blockerade',
                    hold: "Blockerad",
                    reason: "Orsak till blockering",
                },
                orderRows: {
                    title: "Order rader",
                    description: "Order rader associerade med ordern",
                    product: "Produkt",
                    orderedQuantity: "Beställt antal",
                    actualQuantity: "Aktuellt antal",
                    removeRow: "Ta bort rad",
                    editRow: "Redigera rad",
                    addRow: "Lägg till rad",
                },
            }
        },
        user: {
            id: "ID",
            username: "Användarnamn",
            firstName: "Förnamn",
            lastName: "Efternamn",
            name: "Namn",
            email: "E-post",
            phone: "Telefon",
            enabled: "Aktiverad",
            role: "Roll",
            addInstance: "Lägg till instans",
            removeInstance: "Ta bort instans",
            cards: {
                userInfo: {
                    title: "Användarinformation",
                    description: "Grundläggande information om användaren",
                },
                contactInfo: {
                    title: "Kontaktinformation",
                    description: "Kontaktinformation för användaren",
                },
                accountInfo: {
                    title: "Konto",
                    description: "Kontoinställningar och roll",
                },
                password: {
                    title: "Lösenord",
                    description: "Mejla användaren en länk för att välja ett nytt lösenord. Det nuvarande lösenordet fungerar tills de gör det.",
                    send: "Skicka mejl för lösenordsåterställning",
                    confirmTitle: "Skicka lösenordsåterställning?",
                    confirmBody: (who: string) => `${who} får ett mejl med en länk för att välja ett nytt lösenord. Länken gäller i 24 timmar.`,
                    confirm: "Skicka mejl",
                    sent: "Mejlet för lösenordsåterställning är skickat",
                },
                instances: {
                    title: "Instanser",
                    description: "Instanser som användaren har tillgång till",
                },
            },
            search: {
                title: "Användare",
                description: "Sök efter en användare efter användarnamn eller namn",
                search: "Sök",
            },
            validationRules: {
                usernameIsRequired: "Användarnamn krävs",
            },
        },
    },
    importExport: {
        customerImport: "Kundimport",
        worksiteImport: "Arbetsplatsimport",
        status: "Status",
        import: "Importera",
        importAll: "Importera alla rader",
        addRows: "Lägg till 10 rader",
        copyTemplate: "Kopiera mall",
        excelImport: "Dra och släpp Excel/CSV-fil här eller klicka för att ladda upp",
        notYetImported: "Ej importerad",
        imported: "Importerad",
        failed: "Misslyckades",
        customerId: "Kund-ID",
        importing: "Importerar...",
        templateCopied: "Kolumnnamn kopierade till urklipp",
        rowsAdded: "rader tillagda från fil",
        noDataInFile: "Ingen data hittades i filen",
        createFromClipboard: "Skapa från urklipp",
    },
    templateEditor: {
        title: "Mallredigerare",
        description: "Redigera och förhandsgranska e-postmallar",
        selectTemplate: "Mall",
        selectTemplatePlaceholder: "Välj en mall...",
        subject: "Ämne",
        content: "Innehåll",
        preview: "Förhandsgranskning",
        placeholders: "Tillgängliga platshållare",
        placeholdersHint: "Ändra exempelvärdena för att förhandsgranska olika data. Töm ett värde för att se hur {{#sektion}}-rader döljs.",
        placeholdersIgnored: "Förhandsgranskar med en riktig order — exempelvärdena nedan ignoreras.",
        previewOrder: "Förhandsgranska med order (valfritt)",
        previewOrderPlaceholder: "Använd exempelvärden…",
        placeholder: "Platshållare",
        exampleValue: "Exempelvärde",
    },
};